import { SITE } from "@/components/seo/Seo";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, Link2, Loader2, MessageSquareWarning, Share2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CopyButton from "./CopyButton";

const linkOf = (s) => `${SITE}${s.path}`;

export const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export const ViewsLine = ({ s, testid = "share-views" }) => s ? (
  <p data-testid={testid} className="flex items-center gap-1.5 text-xs text-slate-400">
    <Eye className="h-3.5 w-3.5 text-brand-cyan" />{s.views ? `Viewed ${s.views}× · last ${ago(s.last_viewed_at)}` : "Not opened yet"}
  </p>
) : null;

const FeedbackItem = ({ f, testid }) => (
  <div data-testid={testid} className={`flex gap-2 text-sm ${f.action === "approve" ? "text-brand-volt" : "text-amber-300"}`}>
    {f.action === "approve" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0" />}
    <span className="min-w-0 break-words"><b>{f.action === "approve" ? "Approved" : "Changes requested"}</b> by {f.name}
      {f.comment && <span className="block text-slate-300">"{f.comment}"</span>}
      <span className="block text-[11px] text-slate-500">{new Date(f.created_at).toLocaleString()}</span>
    </span>
  </div>
);

export function useShare(jobId) {
  const [state, setState] = useState({ share: null, feedback: [] });
  const load = () => api.get(`/share/${jobId}`).then(({ data }) => setState(data)).catch(() => {});
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, load };
}

export function ClientFeedback({ jobId, share }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const f = share.feedback?.[0];
  if (!f && !share.share) return null;
  const revise = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/engine/refine", { job_id: jobId, message: `Revise the script to address this client feedback from ${f.name}: "${f.comment}"` });
      if (data.mode === "edit" && data.job_id) navigate(`/scripts/${data.job_id}`);
      else { toast.info(data.answer || "No change was needed."); setBusy(false); }
    } catch (e) { toast.error(errorMessage(e, "Couldn't start the revision.")); setBusy(false); }
  };
  return (
    <div data-testid="client-feedback" className="panel space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">Shared with client</p>
        <ViewsLine s={share.share} testid="script-share-views" />
      </div>
      {f && <FeedbackItem f={f} testid="client-feedback-latest" />}
      {f?.action === "changes" && f.comment && (
        <button onClick={revise} disabled={busy} data-testid="revise-feedback-btn" className="btn-brand h-10 w-full text-sm sm:w-auto">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Revise with this feedback
        </button>
      )}
    </div>
  );
}

export function ShareButton({ jobId, share }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await share.load(); } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  const create = () => run(() => api.post(`/share/${jobId}`));
  const revoke = () => run(async () => { await api.delete(`/share/${jobId}`); toast.success("Link revoked"); });
  const s = share.share;
  return (
    <>
      <button onClick={() => { setOpen(true); if (!s) create(); }} data-testid="share-btn" className="btn-ghost h-10 text-sm"><Share2 className="h-4 w-4" /> Share</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] rounded-3xl border-white/10 bg-ink-900 sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Share with client</DialogTitle>
            <DialogDescription className="text-slate-400">A read-only link with your brand name. Clients can approve or request changes. No login needed.</DialogDescription>
          </DialogHeader>
          {busy && !s ? <div className="skeleton h-12" /> : s ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-ink-950 p-2 pl-3">
                <Link2 className="h-4 w-4 shrink-0 text-brand-cyan" />
                <input readOnly value={linkOf(s)} data-testid="share-link-input" className="min-w-0 flex-1 bg-transparent text-sm text-slate-200 outline-none" />
                <CopyButton text={() => linkOf(s)} label="Copy" testid="share-link-copy" className="h-9 shrink-0 text-xs" />
              </div>
              <ViewsLine s={s} />
              <div className="flex flex-wrap gap-2">
                <a href={s.path} target="_blank" rel="noopener noreferrer" className="btn-ghost h-9 text-xs" data-testid="share-open-link">Open link</a>
                <button onClick={revoke} disabled={busy} data-testid="share-revoke-btn" className="btn-ghost h-9 text-xs text-red-300">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Revoke link
                </button>
              </div>
            </div>
          ) : (
            <button onClick={create} disabled={busy} data-testid="share-create-btn" className="btn-brand h-11">Create share link</button>
          )}
          {share.feedback?.length > 0 && (
            <div className="max-h-56 space-y-3 overflow-y-auto border-t border-white/10 pt-4" data-testid="share-feedback-list">
              {share.feedback.map((f, i) => <FeedbackItem key={i} f={f} testid={`share-feedback-item-${i}`} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
